/**
 * Brand colour helpers.
 *
 * A charity's `primaryColor` is stored as a lowercase `#rrggbb` hex. These
 * pure functions turn that one value into everything the donation page and
 * the branded emails need: a hover shade, a light tint, and a text colour
 * that stays readable on top of it. Everything is deterministic so the
 * settings preview, the live donate page and the emails agree.
 */

import type { CSSProperties } from 'react';

/** Dark text used when a brand colour is too light for white text. */
export const DARK_TEXT = '#1a1918';
export const WHITE_TEXT = '#ffffff';

/** WCAG AA for normal-size text. The donate button is 15px, so this applies. */
const MIN_BUTTON_CONTRAST = 4.5;

/**
 * Normalise anything that plausibly denotes a colour into `#rrggbb`.
 * Accepts `#abc`, `#aabbcc`, `#aabbccdd` (alpha dropped), the same without
 * the `#`, and `rgb()`/`rgba()`. Returns null for anything else — named
 * colours, `var(--x)`, gradients, junk.
 */
export function normalizeHex(input: string | null | undefined): string | null {
  if (!input) return null;
  const value = input.trim().toLowerCase();

  const hex = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/);
  if (hex) {
    const digits = hex[1];
    if (digits.length === 3) {
      return '#' + digits.split('').map((d) => d + d).join('');
    }
    return '#' + digits.slice(0, 6);
  }

  const rgb = value.match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*(?:[,/]\s*[\d.%]+\s*)?\)$/);
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, parseInt(n, 10)));
    return '#' + parts.map((n) => n.toString(16).padStart(2, '0')).join('');
  }

  return null;
}

export function isHexColour(value: string | null | undefined): value is string {
  return !!value && /^#[0-9a-f]{6}$/i.test(value);
}

function toRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function toHex(rgb: [number, number, number]): string {
  return '#' + rgb.map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')).join('');
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la >= lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * Mix a colour toward black (negative amount) or white (positive amount).
 * `amount` is -1..1; 0 returns the input unchanged.
 */
export function shade(hex: string, amount: number): string {
  const target = amount < 0 ? 0 : 255;
  const t = Math.min(1, Math.abs(amount));
  const rgb = toRgb(hex).map((c) => c + (target - c) * t) as [number, number, number];
  return toHex(rgb);
}

/**
 * Text colour for a solid button in the given colour. White wherever it
 * meets AA for normal text; otherwise the soft dark; and pure black for
 * the narrow mid-tone band where even the soft dark falls short. Any
 * colour gets at least 4.5:1 against white or black, so this always
 * passes. Trustees never see this rule, they just never get an
 * unreadable button.
 */
export function pickTextColour(bgHex: string): string {
  if (contrastRatio(bgHex, WHITE_TEXT) >= MIN_BUTTON_CONTRAST) return WHITE_TEXT;
  if (contrastRatio(bgHex, DARK_TEXT) >= MIN_BUTTON_CONTRAST) return DARK_TEXT;
  return '#000000';
}

/** Near-white and near-black are never a brand colour worth suggesting. */
export function isNeutral(hex: string): boolean {
  const [r, g, b] = toRgb(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const saturation = max === 0 ? 0 : (max - min) / max;
  const lum = relativeLuminance(hex);
  return saturation < 0.08 || lum > 0.95 || lum < 0.02;
}

/**
 * The CSS custom properties the donation form reads, derived from one
 * brand colour. Returns undefined when there is no valid colour so the
 * caller can spread it straight into a `style` prop and fall back to the
 * TrusteeKit gold defined in globals.css.
 */
export function brandCssVars(primary: string | null | undefined): CSSProperties | undefined {
  if (!isHexColour(primary)) return undefined;
  const base = primary.toLowerCase();
  return {
    '--accent-gold': base,
    '--accent-gold-hover': shade(base, -0.18),
    '--accent-gold-light': shade(base, 0.18),
    '--accent-contrast': pickTextColour(base),
  } as CSSProperties;
}
